<template>
    <div
        class="identify-coordinates mb-3 mb-sm-4">
        <div class="row">
            <div class="col-auto d-flex align-items-center">
                <i class="fas fa-map-marker-alt"></i>
            </div>
            <div class="col d-flex align-items-center px-0">
                <div class="coordinates-wrapper">
                    <div class="row flex-nowrap no-gutters coordinates-editor" v-if="isEditorActive">
                        <div class="col-6">
                            <SimpleInput
                                :readonly="disabled"
                                :disabled="disabled"
                                class="city-bg city-bg-sm mb-0"
                                :id="`input-coordinate-x`"
                                :name="`coordinate-x`"
                                type="number"
                                step="any"
                                :prefix="prefixX"
                                v-model.trim="identify.coordinate.x"
                                :required="true"
                                v-validate="'required|decimal'"
                                :data-vv-scope="vScope"
                                :inputClasses="[{'is-invalid': fnHasError(`coordinate-x`, vScope) }]"
                                :error="fnFirstError(`coordinate-x`, vScope)"/>
                        </div>
                        <div class="col-6">
                            <SimpleInput
                                :readonly="disabled"
                                :disabled="disabled"
                                class="city-bg city-bg-sm mb-0"
                                :id="`input-coordinate-y`"
                                :name="`coordinate-y`"
                                type="number"
                                step="any"
                                :prefix="prefixY"
                                v-model.trim="identify.coordinate.y"
                                :required="true"
                                v-validate="'required|decimal'"
                                :data-vv-scope="vScope"
                                :inputClasses="[{'is-invalid': fnHasError(`coordinate-y`, vScope) }]"
                                :error="fnFirstError(`coordinate-y`, vScope)"/>
                        </div>
                    </div>
                    <div class="row coordinates-text" v-else>
                        <div class="col-12">
                            <div class="coordinates-decimal" v-html="coordinateFormatText"></div>
                        </div>
                    </div>

                </div>
            </div>
            <div class="col-auto d-flex align-items-center coordinates-actions pl-sm-0">
                <button
                    v-if="!isEditorActive"
                    type="button"
                    data-toggle="tooltip-city"
                    :title="$t(`${this.translateID}.copy_to_clipboard.action`)"
                    class="btn btn-sm min-w-auto my-0 mx-auto d-block h-100 unfocus bg-light"
                    @click="copyCoordinateToClipboard">
                    <i class="fas fa-copy"></i>
                </button>
                <button
                    v-if="isEditorActive"
                    type="button"
                    :disabled="!hasChanges"
                    data-toggle="tooltip-city"
                    :title="$t(`${this.translateID}.editor.apply.action`)"
                    class="btn btn-sm btn-link min-w-auto my-0 mx-auto d-block h-100 unfocus apply-changes"
                    @click="onApplyChanges">
                    <i class="fas fa-check"></i>
                </button>
                <button
                    type="button"
                    data-toggle="tooltip-city"
                    :title="$t(`${this.translateID}.editor.action.` + (isEditorActive ? 'hide' : 'show'))"
                    class="btn btn-sm min-w-auto my-0 mx-auto d-block h-100 unfocus"
                    :class="isEditorActive ? 'bg-success text-white' : 'bg-light'"
                    @click="onChangeEditor">
                    <i class="fas fa-pencil-alt"></i>
                </button>
            </div>
        </div>
        <!-- <div class="row mb-3">
            <div class="col-12">
                <p class="small m-0">{{ selectedProjectionName }}</p>
            </div>
        </div> -->
    </div>
</template>

<script>
import GlobalMixin from "@/app/shared/mixins/global";
import { get as getProjection, transform as transformProjection } from "ol/proj";
// import { fromEPSGCode } from 'ol/proj/proj4';
import { degreesToStringHDMS } from 'ol/coordinate';

const DEFAULT_PROJECTION = 'EPSG:4326';

export default {
    name: "IdentifyCoordinates",
    mixins: [GlobalMixin],
    components: {},
    props: {
        moduleCode: {
            type: String,
            default: null
        },
        coordinate: {
            type: Array,
            default: null
        },
        projection: {
            type: String,
            default: null
        },
        resolution: {
            type: Number,
            default: null
        },
        precisionMeters: {
            type: Number,
            default: 4
        },
        templateMeters: {
            type: String,
            default: '<b>X:</b> {x} - <b>Y:</b> {y}'
        },
        precisionDecimalDegrees: {
            type: Number,
            default: 8
        },
        templateDecimalDegrees: {
            type: String,
            default: '<b>Lat:</b> {y} - <b>Lon:</b> {x}'
        },
        vScope: {
            type: String,
            default: 'form'
        },
        baseTranslateID: {
            type: String,
            default: 'identify'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        eventIdentify: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            identify: {
                coordinate_original: {
                    x: 0,
                    y: 0,
                },
                coordinate: {
                    x: 0,
                    y: 0,
                },
                projection: DEFAULT_PROJECTION,
                resolution: null,
                params: {}
            },
            isEditorActive: false,
            displayDMS: false,
        };
    },
    computed: {
        hasChanges() {
            return this.eventIdentify
                && (this.identify.coordinate_original.x != this.identify.coordinate.x || this.identify.coordinate_original.y != this.identify.coordinate.y);
        },
        translateID() {
            return `${this.baseTranslateID}.coordinate`;
        },
        mapProjection() {
            return getProjection(this.projection ?? DEFAULT_PROJECTION);
        },
        viewProjectionCode() {
            return this.$store.getters.getViewProjectionMap() ?? DEFAULT_PROJECTION;
        },
        viewProjection() {
            return getProjection(this.viewProjectionCode);
        },
        viewProjectionUnits() {
            return this.viewProjection ? this.viewProjection.getUnits() : null;
        },
        prefixX() {
            return this.viewProjectionUnits == 'm' ? 'X' : 'Lon';
        },
        prefixY() {
            return this.viewProjectionUnits == 'm' ? 'Y' : 'Lat';
        },
        coordinateFormatText() {
            return this.coordinateFormat();
        }
    },
    mounted() {
        this.setIdentifyInformation();
    },
    methods: {
        setIdentifyInformation() {
            let x = 0;
            let y = 0;
            const _projectionCode = this.projection ?? DEFAULT_PROJECTION;
            const _projection = this.mapProjection;
            if(this.coordinate && this.coordinate.length>1) {
                let _x = isNaN(this.coordinate[0]) ? 0: parseFloat(this.coordinate[0]);
                let _y = isNaN(this.coordinate[1]) ? 0: parseFloat(this.coordinate[1]);
                if (_projection.getAxisOrientation() === 'enu') {
                    x = _x;
                    y = _y;
                }else{
                    x = _y;
                    y = _x;
                }
            }

            const xy = this.transformCoordinates([x, y], _projection, this.viewProjection);
            const _precision = this.viewProjectionUnits == 'm' ? this.precisionMeters : this.precisionDecimalDegrees;

            this.identify.coordinate.x = parseFloat(xy[0].toFixed(_precision));
            this.identify.coordinate.y = parseFloat(xy[1].toFixed(_precision));
            this.identify.coordinate_original.x = this.identify.coordinate.x;
            this.identify.coordinate_original.y = this.identify.coordinate.y;
            this.identify.projection = _projectionCode;
            this.identify.resolution = this.resolution ?? null;
        },
        coordinateFormat(templateMeters = null, templateDecimalDegrees = null) {
            const coord = [this.identify.coordinate.x, this.identify.coordinate.y];
            const projection = this.viewProjection ?? this.identify.projection;
            const units = projection ? projection.getUnits() : null;
            switch(units){
                case 'm':
                    return this.formatCoordinate(coord, templateMeters ?? this.templateMeters, this.precisionMeters);
                case 'degrees':
                    if(this.displayDMS) {
                        // out is now '47° 58′ 60.0″ N, 7° 50′ 60.0″ E'
                        let fractionDigits = 0;
                        return this.formatCoordinate([
                            degreesToStringHDMS('NS', coord[1], fractionDigits),
                            degreesToStringHDMS('EW', coord[1], fractionDigits)
                        ], templateDecimalDegrees ?? this.templateDecimalDegrees, fractionDigits);
                    }else{
                        return this.formatCoordinate(coord, templateDecimalDegrees ?? this.templateDecimalDegrees, this.precisionDecimalDegrees);
                    }
                default:
                    return '';
            }
            // https://stable.demo.geonode.org/catalogue/#/dataset/1825
        },
        formatCoordinate(coordinate, template, fractionDigits) {
          if (coordinate) {
            return template
              .replace('{x}', parseFloat(coordinate[0].toFixed(fractionDigits)))
              .replace('{y}', parseFloat(coordinate[1].toFixed(fractionDigits)));
          }
          return '';
        },
        copyCoordinateToClipboard() {
            let coords = this.coordinateFormat('{x}, {y}', '{y}, {x}');
            const clipBoard = navigator.clipboard;
            clipBoard.writeText(coords)
            .then(() => {
                this.showInfo(`${this.translateID}.copy_to_clipboard.msg.content`, `${this.translateID}.copy_to_clipboard.msg.title`, null, 2000);
            });
        },
        transformCoordinates(coords, source, dest) {
            let _coords = coords && source && dest ? transformProjection(coords, source, dest) : coord;
            return _coords ? _coords.map(val => parseFloat(val)) : _coords;
        },
        onChangeEditor() {
            this.isEditorActive = !this.isEditorActive;
            if(!this.isEditorActive && this.hasChanges) {
                this.resetValidator();
                this.identify.coordinate.x = this.identify.coordinate_original.x;
                this.identify.coordinate.y = this.identify.coordinate_original.y;
            }
        },
        async onApplyChanges() {
            if(this.eventIdentify) {
                await this.$validator
                .validateAll(this.vScope)
                .then(result => {
                    if(!result){
                        this.showError('global.msg.error.form');
                        return;
                    }

                    const coord = this.transformCoordinates([this.identify.coordinate.x, this.identify.coordinate.y], this.viewProjection, this.mapProjection);
                    this.$eventBus.publish(
                        this.moduleCode,
                        this.eventIdentify,
                        {
                            coordinate: coord,
                            resolution: this.resolution,
                            projection: this.projection,
                            params: {}
                        }
                    );
                    this.reset();
                });
            }
        },
        reset() {
            this.resetValidator();
            Object.assign(this.$data.identify, this.$options.data().identify);
            this.isEditorActive = false;
        }
    },
    watch: {
        coordinate: {
            immediate: false,
            handler: function(value) {
                this.setIdentifyInformation();
            }
        },
        projection: {
            immediate: false,
            handler: function(value) {
                this.setIdentifyInformation();
            }
        },
        isEditorActive: {
            immediate: false,
            handler: function(value) {
                this.activeTooltip();
            }
        },
        // viewProjectionCode: {
        //     immediate: false,
        //     handler: function(value) {
        //         this.setIdentifyInformation();
        //     }
        // },
    }
};
</script>

<style scoped lang="scss">
.identify-coordinates {
    .coordinates-actions {
        .btn {
            font-size: 1rem;
            padding: .3rem .6rem;

            &.apply-changes {
                font-size: 1.2rem;
            }
        }
    }
}
</style>
